import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { TaskItem, TaskList, TaskNotification } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { MemberRow, taskApi, taskErrorMessage } from '../../utils/taskApi';
import { MAX_DEPTH, PROJECT_MAX_DEPTH, subtreeOf } from '../../utils/taskTree';
import { projectItemUrl } from '../../utils/taskLinks';
import ListCard from './ListCard';
import Masonry from './Masonry';
import NotificationBanner from './NotificationBanner';
import ShareDialog from './ShareDialog';
import TaskDialog from './TaskDialog';
import SchedulesDialog from './SchedulesDialog';
import TaskIcon from './TaskIcon';
import LoadingSpinner from '../LoadingSpinner';
import '../../tasks.css';

const MINE_KEY = 'lists.mineOnly';

function readMine(): boolean {
  try {
    return localStorage.getItem(MINE_KEY) === '1';
  } catch {
    return false;
  }
}

interface ArchiveData {
  lists: TaskList[];
  items: TaskItem[];
  members: MemberRow[];
}

const ListsPage: React.FC = () => {
  const {
    ready, error, lists: allLists, items: allItems, itemsForList, members, weeklyListId, notifications, markSeen, me,
    createList, deleteList, openComposer, lastError, clearError, refresh, pinned, setPinned, canEditList,
  } = useTasks();
  const location = useLocation();
  const navigate = useNavigate();

  const [mineOnly, setMineOnly] = useState(readMine);
  const [search, setSearch] = useState('');
  const [shareTarget, setShareTarget] = useState<TaskList | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskList | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [freshId, setFreshId] = useState<number | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archive, setArchive] = useState<ArchiveData | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [showSchedules, setShowSchedules] = useState(false);

  // What's new since you last looked: captured on arrival and kept for this
  // visit (banner + "New" glow), while the server copy is marked seen.
  const [banner, setBanner] = useState<TaskNotification[]>([]);
  const [highlight, setHighlight] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!ready || !notifications.length) return;
    setBanner((prev) => {
      const known = new Set(prev.map((n) => n.id));
      const fresh = notifications.filter((n) => !known.has(n.id));
      return fresh.length ? [...fresh, ...prev] : prev;
    });
    setHighlight((prev) => {
      const next = new Set(prev);
      notifications.forEach((n) => n.item_id && next.add(n.item_id));
      return next;
    });
    const ids = notifications.map((n) => n.id);
    const timer = setTimeout(() => markSeen(ids), 1500);
    return () => clearTimeout(timer);
  }, [ready, notifications, markSeen]);

  const setMine = (value: boolean) => {
    setMineOnly(value);
    try {
      localStorage.setItem(MINE_KEY, value ? '1' : '0');
    } catch {
      /* per-viewer convenience only */
    }
  };

  /** Scrolls to an item (or its list) and pulses it. */
  const reveal = useCallback((itemId: number | null, listId: number | null) => {
    setMine(false);
    setSearch('');
    if (itemId) setFlashId(itemId);
    window.setTimeout(() => {
      const el =
        (itemId ? document.querySelector(`[data-item-id="${itemId}"]`) : null) ||
        (listId ? document.querySelector(`[data-list-id="${listId}"]`) : null);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    window.setTimeout(() => setFlashId(null), 2600);
  }, []);

  // Deep link: /lists?item=123 (from the Calendar, the Dashboard card, emails).
  // An item that lives in a project opens there instead.
  useEffect(() => {
    if (!ready) return;
    const params = new URLSearchParams(location.search);
    const item = Number(params.get('item')) || null;
    const list = Number(params.get('list')) || null;
    if (!item && !list) return;
    const home = item ? allLists.find((l) => l.id === allItems.find((i) => i.id === item)?.list_id) : undefined;
    if (item && home?.kind === 'project') {
      navigate(projectItemUrl(home.id, item), { replace: true });
      return;
    }
    reveal(item, list);
    navigate(location.pathname, { replace: true });
  }, [ready, location.search, location.pathname, navigate, reveal, allLists, allItems]);

  // Some lists I can open only from a week-list line (a warehouse request, as
  // an admin; any list with a task on the week list, read-only). Only the
  // lists I'm actually on belong on my Lists page. Projects live on their own
  // page; the branches people chose to show here are cards of kind 'view'.
  const lists = useMemo(
    () =>
      allLists.filter(
        (l) => l.kind !== 'project' && (l.is_team || l.owner_email === me || (members.get(l.id) || []).includes(me))
      ),
    [allLists, members, me]
  );

  /**
   * What a card shows: its own items, or — for a project branch — the
   * project's items under that branch, edited in place. Three levels show; a
   * deeper line gets a "+N" chip that opens the project.
   */
  const contentFor = (list: TaskList) => {
    if (list.kind !== 'view') return { items: itemsForList(list.id) };
    const project = allLists.find((l) => l.id === list.view_list_id && l.kind === 'project');
    if (!project) return { items: [], readOnly: true };
    const all = itemsForList(project.id);
    const root = list.view_root_item_id ? all.find((i) => i.id === list.view_root_item_id) || null : null;
    if (list.view_root_item_id && !root) return { items: [], readOnly: true };
    const offset = root ? root.depth + 1 : 0;
    if (list.view_filter === 'materials') {
      // The shopping list: every material under the branch (or in the
      // project), as one flat list; new lines are materials.
      const materials = subtreeOf(all, root ? root.id : null)
        .filter((i) => i.node_kind === 'material')
        .map((i) => ({ ...i, parent_id: root?.id ?? null }));
      return {
        items: materials,
        readOnly: !canEditList(list),
        source: { project, root, filter: 'materials' as const },
        shape: { maxDepth: 0, rootParent: root?.id ?? null, depthOffset: offset },
        flat: true,
        newItemKind: 'material' as const,
      };
    }
    return {
      items: subtreeOf(all, root ? root.id : null),
      readOnly: !canEditList(list),
      source: { project, root },
      shape: { maxDepth: Math.max(0, Math.min(MAX_DEPTH, PROJECT_MAX_DEPTH - offset)), rootParent: root?.id ?? null, depthOffset: offset },
    };
  };

  const weekly = useMemo(
    () => lists.find((l) => l.id === weeklyListId) || lists.find((l) => l.kind === 'weekly') || null,
    [lists, weeklyListId]
  );
  // Order: the MPP week list, then my pinned lists (in pin order), then the rest.
  const pinnedLists = useMemo(
    () => pinned.map((id) => lists.find((l) => l.id === id)).filter((l): l is TaskList => !!l && l.id !== weekly?.id),
    [pinned, lists, weekly]
  );
  const others = useMemo(
    () =>
      lists
        .filter((l) => l.id !== weekly?.id && !pinned.includes(l.id))
        .sort((a, b) => (a.id === freshId ? -1 : b.id === freshId ? 1 : b.created_at.localeCompare(a.created_at))),
    [lists, weekly, freshId, pinned]
  );
  const togglePin = (list: TaskList) => setPinned(list.id, !pinned.includes(list.id));

  const newList = async () => {
    const list = await createList({ title: '' });
    if (list) {
      setFreshId(list.id);
      setMine(false);
      setSearch('');
    }
  };

  // Same rule as the live lists: only lists I'm on. Archived projects (and
  // their cards) are on the Projects page.
  const loadArchive = useCallback(async () => {
    const data = await taskApi.archived();
    const onIt = (l: TaskList) =>
      (l.kind === 'standard' || l.kind === 'weekly') &&
      (l.is_team || l.owner_email === me || data.members.some((m) => m.list_id === l.id && m.email === me));
    setArchive({ ...data, lists: data.lists.filter(onIt) });
  }, [me]);

  // A list archived or restored elsewhere on the page changes what belongs here.
  const activeCount = lists.length;
  useEffect(() => {
    if (!archiveOpen) return;
    loadArchive().catch(() => undefined);
  }, [archiveOpen, activeCount, loadArchive]);

  const toggleArchive = async () => {
    const next = !archiveOpen;
    setArchiveOpen(next);
    if (next && !archive) {
      setArchiveError(null);
      try {
        await loadArchive();
      } catch (e) {
        setArchiveError(taskErrorMessage(e, 'Could not load the archive'));
      }
    }
  };

  // An empty list has nothing to lose, so it goes without a confirmation.
  const requestDelete = (list: TaskList) => {
    const empty = list.kind === 'view' || (!itemsForList(list.id).length && !list.images.length);
    if (empty) deleteList(list.id);
    else setDeleteTarget(list);
  };

  if (!ready) {
    if (error) {
      return (
        <div className="tk-page">
          <div className="tk-setup">
            <h2>Lists aren't available yet</h2>
            <p>{error}</p>
            <p className="tk-muted">
              If this is a fresh install, run <code>db/tasks-schema.sql</code> against the Neon database, then reload.
            </p>
            <button type="button" className="tk-btn tk-btn-primary" onClick={refresh}>Try again</button>
          </div>
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  const cardProps = {
    highlightIds: highlight,
    flashId,
    mineOnly,
    search,
    onOpenImage: setLightbox,
    onShare: setShareTarget,
    onDelete: requestDelete,
  };

  return (
    <div className="tk-page">
      <header className="tk-page-head">
        <div>
          <h1 className="tk-page-title">Lists</h1>
          <p className="tk-page-sub">Shared to-dos for the whole team. Unfinished items roll into next week's list every Monday.</p>
        </div>
        <div className="tk-page-actions">
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => openComposer({ when: 'date' })}>
            <TaskIcon name="scheduled" size={15} /> <span className="tk-hide-sm">Schedule</span>
          </button>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setShowSchedules(true)} title="Scheduled & recurring tasks, including paused ones">
            <TaskIcon name="recurring" size={15} /> <span className="tk-hide-sm">Scheduled</span>
          </button>
          <button type="button" className="tk-btn tk-btn-primary" onClick={newList}>
            <TaskIcon name="plus" size={15} /> New list
          </button>
        </div>
      </header>

      <div className="tk-toolbar">
        <label className="tk-search">
          <TaskIcon name="search" size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search lists" aria-label="Search lists" />
          {search && (
            <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={() => setSearch('')} aria-label="Clear search">
              <TaskIcon name="close" size={13} />
            </button>
          )}
        </label>
        <div className="tk-segmented" role="group" aria-label="Filter">
          <button type="button" className={!mineOnly ? 'is-active' : ''} onClick={() => setMine(false)}>Everything</button>
          <button type="button" className={mineOnly ? 'is-active' : ''} onClick={() => setMine(true)}>Assigned to me</button>
        </div>
      </div>

      {lastError && (
        <div className="tk-error-bar" role="alert">
          <span>{lastError}</span>
          <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={clearError} aria-label="Dismiss"><TaskIcon name="close" size={13} /></button>
        </div>
      )}

      <NotificationBanner
        notifications={banner}
        onShow={(n) =>
          allLists.find((l) => l.id === n.list_id)?.kind === 'project'
            ? navigate(projectItemUrl(n.list_id!, n.item_id))
            : reveal(n.item_id, n.list_id)
        }
        onDismiss={(id) => setBanner((prev) => prev.filter((n) => n.id !== id))}
        onDismissAll={() => setBanner([])}
      />

      {weekly && (
        <section className="tl-pinned" aria-label="Pinned">
          <ListCard list={weekly} items={itemsForList(weekly.id)} members={[]} featured {...cardProps} />
        </section>
      )}

      {pinnedLists.length > 0 && (
        <section aria-label="Pinned lists">
          <h2 className="tk-section-label">Pinned</h2>
          <Masonry>
            {pinnedLists.map((list) => (
              <ListCard
                key={list.id}
                list={list}
                members={members.get(list.id) || []}
                isPinned
                onTogglePin={togglePin}
                {...cardProps}
                {...contentFor(list)}
              />
            ))}
          </Masonry>
        </section>
      )}

      {others.length > 0 && (
        <section aria-label="Lists">
          <h2 className="tk-section-label">Lists</h2>
          <Masonry>
            {others.map((list) => (
              <ListCard
                key={list.id}
                list={list}
                members={members.get(list.id) || []}
                autoFocusTitle={list.id === freshId}
                onTogglePin={togglePin}
                {...cardProps}
                {...contentFor(list)}
              />
            ))}
          </Masonry>
        </section>
      )}

      <section className="tl-archive">
        <button type="button" className="tl-section-toggle" onClick={toggleArchive} aria-expanded={archiveOpen}>
          <TaskIcon name="chevron-down" size={14} className={archiveOpen ? '' : 'tl-rot'} />
          <TaskIcon name="archive" size={14} /> Archive &amp; past weeks
        </button>
        {archiveOpen && (
          <>
            {archiveError && <p className="tl-error">{archiveError}</p>}
            {!archive && !archiveError && <p className="tk-muted">Loading…</p>}
            {archive && !archive.lists.length && <p className="tk-muted">Nothing archived yet.</p>}
            {archive && archive.lists.length > 0 && (
              <Masonry>
                {archive.lists.map((list) => (
                  <ListCard
                    key={list.id}
                    list={list}
                    items={archive.items.filter((i) => i.list_id === list.id)}
                    members={archive.members.filter((m) => m.list_id === list.id).map((m) => m.email)}
                    readOnly
                    {...cardProps}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </Masonry>
            )}
          </>
        )}
      </section>

      <button type="button" className="tk-fab" onClick={() => openComposer({})} aria-label="New task">
        <TaskIcon name="plus" size={22} />
      </button>

      {showSchedules && <SchedulesDialog onClose={() => setShowSchedules(false)} />}

      {shareTarget && (
        <ShareDialog list={shareTarget} members={members.get(shareTarget.id) || []} onClose={() => setShareTarget(null)} />
      )}

      {deleteTarget && (
        <TaskDialog
          title={`Delete "${deleteTarget.title || 'Untitled list'}"?`}
          onClose={() => setDeleteTarget(null)}
          footer={
            <>
              <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                type="button"
                className="tk-btn tk-btn-danger"
                onClick={() => {
                  deleteList(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p>The list, its items and photos are removed for everyone it's shared with.</p>
        </TaskDialog>
      )}

      {lightbox &&
        createPortal(
          <div className="tk-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-label="Photo">
            <img src={lightbox} alt="" />
            <button type="button" className="tk-lightbox-close" aria-label="Close"><TaskIcon name="close" size={20} /></button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ListsPage;
